import { demoGroups } from '../data/demo';
import type { ChatMessage, Group, SearchFilters } from '../types';

const LOCAL_GROUPS_KEY = 'undonghal-saram-local-groups';
const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
const MEMBER_KEY = 'undonghal-saram-member-id';
const NICKNAME_KEY = 'undonghal-saram-nickname';

export function chatIdentity() {
  let memberId = localStorage.getItem(MEMBER_KEY);
  let nickname = localStorage.getItem(NICKNAME_KEY);
  if (!memberId) { memberId = crypto.randomUUID(); localStorage.setItem(MEMBER_KEY, memberId); }
  if (!nickname) { nickname = `운동친구${Math.floor(1000 + Math.random() * 9000)}`; localStorage.setItem(NICKNAME_KEY, nickname); }
  return { memberId, nickname };
}

function localGroups(): Group[] {
  try { return JSON.parse(localStorage.getItem(LOCAL_GROUPS_KEY) || '[]') as Group[]; }
  catch { return []; }
}

function matches(group: Group, filters: SearchFilters) {
  const sportMatch = filters.anySport || filters.sports.length === 0 || filters.sports.some((id) => group.sports.includes(id));
  const areaMatch = filters.area.startsWith('내 주변') || filters.area.includes('전체') || group.area.includes(filters.area.replace(/^(서울|경기) /, '')) || group.area === filters.area;
  return sportMatch && areaMatch;
}

export async function getGroups(filters: SearchFilters): Promise<{ groups: Group[]; demo: boolean }> {
  const params = new URLSearchParams({ sports: filters.sports.join(','), any: String(filters.anySport), area: filters.area, when: filters.when });
  try {
    const response = await fetch(`${API_BASE}/api/groups?${params}`, { signal: AbortSignal.timeout(2500) });
    if (!response.ok) throw new Error('API unavailable');
    return { groups: await response.json() as Group[], demo: false };
  } catch {
    return { groups: [...localGroups(), ...demoGroups].filter((group) => matches(group, filters)), demo: true };
  }
}

export async function createGroup(group: Group): Promise<{ group: Group; demo: boolean }> {
  try {
    const response = await fetch(`${API_BASE}/api/groups`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ...group, memberId: chatIdentity().memberId }), signal: AbortSignal.timeout(3000) });
    if (!response.ok) throw new Error('API unavailable');
    return { group: await response.json() as Group, demo: false };
  } catch {
    localStorage.setItem(LOCAL_GROUPS_KEY, JSON.stringify([group, ...localGroups()]));
    return { group, demo: true };
  }
}

export async function joinGroup(id: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/api/groups/${encodeURIComponent(id)}/join`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ memberId: chatIdentity().memberId }), signal: AbortSignal.timeout(3000) });
    return response.ok;
  } catch { return true; }
}

export async function getMessages(groupId: string): Promise<ChatMessage[]> {
  const response = await fetch(`${API_BASE}/api/groups/${encodeURIComponent(groupId)}/messages`, { signal: AbortSignal.timeout(4000) });
  if (!response.ok) throw new Error('채팅을 불러오지 못했습니다.');
  return response.json() as Promise<ChatMessage[]>;
}

export async function sendMessage(groupId: string, body: string): Promise<ChatMessage> {
  const identity = chatIdentity();
  const response = await fetch(`${API_BASE}/api/groups/${encodeURIComponent(groupId)}/messages`, {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...identity, body }), signal: AbortSignal.timeout(4000)
  });
  if (!response.ok) throw new Error('메시지를 보내지 못했습니다.');
  return response.json() as Promise<ChatMessage>;
}
